<script setup lang="ts">
  import type { Agency } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { agencyKey } from '@gat-ship/types/injection-keys'
  import { inject } from 'vue'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyProvider = inject(agencyKey)
  const description = ref('')

  const changeAgencyNameSchema = z.object({
    agencyId: z.string().min(1),
    name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
    shortName: z
      .string()
      .min(1, 'Short Name is required')
      .max(120, 'Max 120 characters')
  })
  type ChangeAgencyNameFormValues = z.infer<typeof changeAgencyNameSchema>

  const form = useForm({
    defaultValues: {
      agencyId: '',
      name: '',
      shortName: ''
    } as ChangeAgencyNameFormValues,
    validators: {
      onChange: changeAgencyNameSchema
    },
    onSubmit: async ({ value }) => {
      await agencyProvider?.commands.changeName(value.agencyId, {
        name: value.name,
        shortName: value.shortName
      })
      isOpen.value = false
    }
  })

  export interface ShowChangeAgencyNameModalParams {
    agency: Agency
  }

  function showModal({ agency }: ShowChangeAgencyNameModalParams) {
    description.value = agency.name
    form.reset({
      agencyId: agency.id,
      name: agency.name,
      shortName: agency.shortName
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle> Change Agency Name </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.agency.modal.changeName.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="shortName" label="Short Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.agency.modal.changeName.shortNameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
